import { lazy } from "react";
import type { FC, LazyExoticComponent } from "react";

/**
 * Dashboard report registry — the single source of truth for which BI reports
 * exist in the Dashboard tab (the in-house Power BI replacement).
 *
 * Adding a migrated Power BI report later = append ONE entry here + one
 * component file under ./reports/. The left rail, routing, and empty/placeholder
 * states all read from this array.
 *
 * See: projects/nexus-dashboards-powerbi-migration/docs/design.md §3.2
 */
export interface DashboardReport {
  id: string;
  title: string;
  group?: string;
  description?: string;
  status: "ready" | "coming-soon";
  /**
   * The lazy-loaded report component. A default-export `React.FC` with NO props.
   * Required for `status: "ready"` reports; omitted for "coming-soon" stubs
   * (the page rail renders a "Coming soon" tile for those instead).
   */
  component?: LazyExoticComponent<FC>;
}

export const REPORTS: DashboardReport[] = [
  {
    id: "sales",
    title: "Sales",
    group: "Finance",
    status: "ready",
    description: "Invoice sales, credits, and trends",
    component: lazy(() => import("./reports/SalesReport")),
  },
  // Future migrated Power BI reports appended here (one line each).
  // Stubbed as "coming-soon" so users see the full future catalog (anti
  // new-tool-anxiety) until each report's component + data layer lands.
  {
    id: "compare-invoice",
    title: "Compare Invoice",
    group: "Finance",
    status: "coming-soon",
    description: "Invoice comparison across periods",
  },
  {
    id: "outstanding-invoices",
    title: "Outstanding Invoices",
    group: "Finance",
    status: "coming-soon",
    description: "Open / unpaid invoices",
  },
  {
    id: "subsidy-tracker",
    title: "Subsidy Tracker",
    group: "Finance",
    status: "coming-soon",
    description: "Subsidy commitments and drawdown",
  },
];

/** First report ready to render — the default target for `/dashboard`. */
export const firstReadyReport = (): DashboardReport | undefined =>
  REPORTS.find((r) => r.status === "ready");

export const getReport = (id: string | undefined): DashboardReport | undefined =>
  id ? REPORTS.find((r) => r.id === id) : undefined;
